import type { BundledLanguage } from "shiki";

/**
 * Single source of truth for "what kind of file is this, and where does it go".
 *
 * Three separate registries used to answer overlapping questions and disagreed
 * with each other: EDITABLE_TEXT_EXTS in api/chat/attachments.ts (can edit_file
 * touch it), CODE_FILE_LANGUAGES in doc-preview-panel.tsx and LANGUAGE_BY_EXT in
 * container-files-panel.tsx (how to highlight it). An AutoLISP `.lsp` was
 * highlightable in one panel, unknown to the other, and not editable anywhere
 * because the editable set listed `lisp` but not `lsp`. Engineering formats —
 * 12d macros, GXL — fell through different cracks in each.
 *
 * A file has exactly one destination. The two extension sets below are disjoint
 * by construction; `attachmentDestination` is the only thing that reads them.
 */

/** Lowercase extension without the dot, or "" when there isn't one. */
export function extOf(filename: string): string {
  const dot = filename.lastIndexOf(".");
  if (dot <= 0 || dot === filename.length - 1) return "";
  return filename.slice(dot + 1).toLowerCase();
}

/**
 * Source and data files that belong in the code execution sandbox as real
 * files, so the model can run, parse or patch them in place rather than
 * regenerating them. Reaching the container requires Anthropic + code
 * execution; without that these fall back to text extraction and stay readable,
 * just not executable.
 *
 * Must not overlap EDITABLE_TEXT_EXTS — a file in both would be edited through
 * two independent filesystems that never reconcile.
 */
export const CONTAINER_UPLOAD_EXTS = new Set([
  // Tabular / data — too big and too regular to read as prose
  "csv", "tsv", "xls", "xlsx",
  // Survey, CAD and civil engineering
  "lsp", "lisp", "4dm", "4d", "12xml", "gxl",
  // Scripting the sandbox can actually run
  "py", "ipynb", "sql", "sh", "bash", "r",
  // Word documents. Users supply these as templates to fill (e.g. a council's
  // form); text extraction strips the logo, headers, fonts and styles, so the
  // model can only rebuild a look-alike unless it has the original to edit.
  "docx",
]);

// Deliberately NOT here: ts/tsx/js/jsx/c/cpp/java/go/rb/rs and friends. The
// sandbox cannot build them, so routing them there would only cost a tool
// round-trip before the model could read code that fits in the prompt anyway.
// They fall through to text extraction and stay directly readable.

/**
 * MIME types that route to the container regardless of extension. Kept because
 * browsers report spreadsheets by type reliably, while engineering formats
 * (.lsp, .4dm, .gxl) usually arrive as application/octet-stream or "" and can
 * only be identified by extension.
 */
export const CONTAINER_UPLOAD_TYPES = new Set([
  "text/csv",
  "application/csv",
  "application/vnd.ms-excel",                                          // .xls (also some browsers' CSV type)
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx
]);

/**
 * Plain-text document formats that load into the request-scoped widget
 * fileStore, making them editable with edit_file and renderable in the preview
 * panel. These are the things Woods AI *produces for* the user, as opposed to
 * source files it works on.
 */
export const EDITABLE_TEXT_EXTS = new Set([
  "html", "htm", "svg", "md", "mdx", "txt", "xml", "json", "yml", "yaml",
]);

/** Where an attachment should be sent. Exactly one applies. */
export type AttachmentDestination = "container" | "widget" | "text";

/** HTML pages, which the user can attach either as source code or as a rendered page. */
export const HTML_EXTS = new Set(["html", "htm"]);

export function isHtmlFile(file: { name: string; type: string }): boolean {
  return file.type === "text/html" || HTML_EXTS.has(extOf(file.name));
}

/**
 * Per-attachment routing set by the client and persisted on the file part, so
 * history replays route the file the same way every turn. Namespaced under
 * `woods` because providers only read their own key from providerMetadata.
 */
export const CONTAINER_ATTACHMENT_METADATA = { woods: { sendToContainer: true } } as const;

/** True when the client asked for this attachment to go to the code execution container. */
export function requestsContainer(part: { providerMetadata?: unknown }): boolean {
  const woods = (part.providerMetadata as { woods?: { sendToContainer?: unknown } } | undefined)?.woods;
  return woods?.sendToContainer === true;
}

/**
 * Resolves an attachment to its single destination.
 *
 * `container` wins over `widget` when both could claim a file, but only when
 * the container is actually reachable — otherwise a code file would be routed
 * to a sandbox that isn't there and silently lose its text.
 */
export function attachmentDestination(
  filename: string | undefined,
  mediaType: string | undefined,
  {
    containerAvailable,
    forceContainer = false,
    containerRequested = false,
  }: { containerAvailable: boolean; forceContainer?: boolean; containerRequested?: boolean },
): AttachmentDestination {
  const ext = filename ? extOf(filename) : "";

  if (containerAvailable) {
    // Code mode: the sandbox is the only filesystem. Everything goes there so
    // a file is never simultaneously prompt text and a file on disk — the two
    // drift the moment either side is edited.
    if (forceContainer || containerRequested) return "container";
    if (mediaType && CONTAINER_UPLOAD_TYPES.has(mediaType)) return "container";
    if (ext && CONTAINER_UPLOAD_EXTS.has(ext)) return "container";
  }
  if (ext && EDITABLE_TEXT_EXTS.has(ext)) return "widget";
  return "text";
}

/**
 * Largest PDF Anthropic will read as a document block. A bigger one fails the
 * whole request with a 400 "The file exceeds the maximum allowed size." — and
 * because history is replayed every turn, every later turn in that chat fails
 * too. Decimal megabytes so we err on the side of rejecting, not failing.
 */
export const MAX_PDF_ATTACHMENT_BYTES = 32_000_000;

/** "49.6 MB" — decimal, to match how the limit is quoted. */
export function formatMegabytes(bytes: number): string {
  return `${(bytes / 1_000_000).toFixed(1)} MB`;
}

/**
 * User-facing reason a file can't be attached to a chat, or null when it can.
 * Only PDFs are capped: spreadsheets and source files go to the sandbox,
 * which takes far larger files.
 */
export function chatAttachmentRejection(file: {
  name: string;
  type: string;
  size: number;
}): string | null {
  const isPdf = file.type === "application/pdf" || extOf(file.name) === "pdf";
  if (isPdf && file.size > MAX_PDF_ATTACHMENT_BYTES) {
    return (
      `${file.name} is ${formatMegabytes(file.size)} — PDFs in chat are limited to ` +
      `${formatMegabytes(MAX_PDF_ATTACHMENT_BYTES)}. Split or compress it, or add it ` +
      `to the Library, which handles large PDFs.`
    );
  }
  return null;
}

/**
 * Extension → shiki language, shared by every panel that renders file content.
 * Covers the engineering formats the language-specific maps each missed: 12d
 * macros read as C, 12d XML and GXL as XML, AutoLISP under both `.lsp` and
 * `.lisp`.
 */
export const LANGUAGE_BY_EXT: Record<string, BundledLanguage> = {
  // Survey, CAD and civil engineering
  lsp: "lisp",
  lisp: "lisp",
  "4dm": "c",
  "4d": "c",
  "12xml": "xml",
  gxl: "xml",
  // General source
  bash: "shell",
  c: "c",
  cpp: "cpp",
  cs: "csharp",
  css: "css",
  go: "go",
  h: "c",
  htm: "html",
  html: "html",
  java: "java",
  js: "javascript",
  json: "json",
  jsx: "jsx",
  md: "markdown",
  mdx: "mdx",
  mjs: "javascript",
  py: "python",
  r: "r",
  rb: "ruby",
  rs: "rust",
  sh: "shell",
  sql: "sql",
  svg: "xml",
  ts: "typescript",
  tsx: "tsx",
  xml: "xml",
  yaml: "yaml",
  yml: "yaml",
  csv: "csv",
  tsv: "csv",
};

/**
 * shiki types `text` as a SpecialLanguage rather than a BundledLanguage, so the
 * fallback is cast. It renders as unhighlighted plain text.
 */
export const PLAIN_TEXT = "text" as BundledLanguage;

/** Language for a path, falling back to unhighlighted plain text. */
export function languageForPath(path: string): BundledLanguage {
  return LANGUAGE_BY_EXT[extOf(path)] ?? PLAIN_TEXT;
}
